// MinimalPrintPage.js

import React, { useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import API from '../services/api';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useUser } from '../contexts/UserContext';
import { generateBusinessHeader, openPrintWindow } from '../utils/printUtils';

function MinimalPrintPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const filteredGroup = params.get('group');
  const groupNameParam = params.get('groupName');
  const orderingParam = params.get('ordering') || 'ClientName';

  // Parse multiple debt filters from JSON
  const debtFiltersParam = React.useMemo(() => {
    const raw = params.get('debtFilters');
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    }
    return [];
  }, [location.search]);

  // 1) Grab userProfile to read dynamic config
  const { userProfile } = useUser();
  const businessProfile = userProfile?.group?.business_profile || {};
  const uiConfig = userProfile?.ui_config || {};

  // a) which client fields to print in the table?
  const printClientDetails = uiConfig.client_list_page?.print_client_details || [];
  const clientFieldsSchema = uiConfig.client_fields_schema || {};

  // b) which debt fields to print in the table?
  const debtFields = uiConfig.debt_fields || [];
  const debtFieldsSchema = uiConfig.debt_fields_schema || {};

  // 2) Fetch clients with full data (including group metadata for filtering)
  const {
    data: clients = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['clients', 'full'],
    queryFn: async () => {
      const response = await API.get('/clients/', { params: { light: false } });
      return response.data;
    },
  });

  // 3) Filter logic with debt filtering and dynamic ordering
  const filteredClients = useMemo(() => {
    let filtered = clients;

    // Apply group filters
    if (filteredGroup) {
      if (filteredGroup === 'NO_GROUP') {
        filtered = filtered.filter(
          (client) =>
            !client.FlowGroup &&
            !client.YearGroup &&
            (!client.GeneralGroups || client.GeneralGroups.length === 0)
        );
      } else if (filteredGroup === 'NO_FLOW') {
        filtered = filtered.filter((client) => !client.FlowGroup);
      } else if (filteredGroup === 'NO_YEAR') {
        filtered = filtered.filter((client) => !client.YearGroup);
      } else if (filteredGroup === 'NO_GENERAL') {
        filtered = filtered.filter(
          (client) => !client.GeneralGroups || client.GeneralGroups.length === 0
        );
      } else {
        filtered = filtered.filter((client) => {
          const groups = [];
          if (client.FlowGroup) groups.push(String(client.FlowGroup.GroupID));
          if (client.YearGroup) groups.push(String(client.YearGroup.GroupID));
          if (client.GeneralGroups && client.GeneralGroups.length > 0) {
            groups.push(...client.GeneralGroups.map((g) => String(g.GroupID)));
          }
          return groups.includes(filteredGroup);
        });
      }
    }

    // Apply multiple debt threshold filters (AND logic)
    if (debtFiltersParam.length > 0) {
      filtered = filtered.filter((client) => {
        // Client must pass ALL filters
        return debtFiltersParam.every((filter) => {
          if (!filter.field || !filter.threshold) return true;

          const threshold = parseFloat(filter.threshold);
          if (isNaN(threshold)) return true;

          let debtValue = 0;
          if (filter.field === 'TotalDebt') {
            debtValue = parseFloat(client?.debt?.TotalDebt || 0);
          } else {
            debtValue = parseFloat(client?.debt?.DebtFields?.[filter.field] || 0);
          }
          return debtValue >= threshold;
        });
      });
    }

    // Apply dynamic ordering based on orderingParam
    const isDescending = orderingParam.startsWith('-');
    const fieldName = isDescending ? orderingParam.slice(1) : orderingParam;

    filtered = [...filtered].sort((a, b) => {
      let valueA, valueB;

      if (fieldName === 'last_interaction_at') {
        // Date sort. Nulls sink to the bottom in descending order (typical
        // "recent first" expectation) and float to the top in ascending.
        const tA = a.last_interaction_at ? new Date(a.last_interaction_at).getTime() : null;
        const tB = b.last_interaction_at ? new Date(b.last_interaction_at).getTime() : null;
        if (tA === null && tB === null) return 0;
        if (tA === null) return 1;   // a sinks
        if (tB === null) return -1;  // b sinks
        const diff = tA - tB;
        return isDescending ? -diff : diff;
      } else if (fieldName === 'TotalDebt') {
        valueA = parseFloat(a?.debt?.TotalDebt || 0);
        valueB = parseFloat(b?.debt?.TotalDebt || 0);
      } else if (debtFieldsSchema[fieldName]) {
        // It's a debt field
        valueA = parseFloat(a?.debt?.DebtFields?.[fieldName] || 0);
        valueB = parseFloat(b?.debt?.DebtFields?.[fieldName] || 0);
      } else {
        // Client field (e.g., ClientName)
        valueA = a[fieldName] || '';
        valueB = b[fieldName] || '';

        // Use Intl.Collator for string comparison
        if (typeof valueA === 'string' && typeof valueB === 'string') {
          const collator = new Intl.Collator(['he', 'ar', 'en'], {
            numeric: true,
            sensitivity: 'base'
          });
          const result = collator.compare(valueA, valueB);
          return isDescending ? -result : result;
        }
      }

      // Numeric comparison
      const diff = valueA - valueB;
      return isDescending ? -diff : diff;
    });

    return filtered;
  }, [clients, filteredGroup, debtFiltersParam, orderingParam, debtFieldsSchema]);

  // Guard against multiple invocations: useEffect re-fires whenever
  // `filteredClients` is a fresh ref (it depends on `debtFieldsSchema`, which
  // is rebuilt on every render), so without this ref the print + navigate
  // could fire more than once per mount.
  const hasPrintedRef = useRef(false);

  // 4) On load, open new window & print the filtered clients
  useEffect(() => {
    if (!isLoading && !isError && !hasPrintedRef.current) {
      const timer = setTimeout(() => {
        if (hasPrintedRef.current) return;
        hasPrintedRef.current = true;
        printClientsInNewWindow(filteredClients);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [isLoading, isError, filteredClients]);

  // ==================== PRINT LOGIC ====================
  const printClientsInNewWindow = (clientsData) => {
    const htmlContent = generatePrintHTML(clientsData);
    openPrintWindow(htmlContent);
    // Return to /clients explicitly rather than `navigate(-1)`. The history
    // entry for this print page is replaced so the back button skips it.
    navigate('/clients', { replace: true });
  };

  // Helper: get the label from the schema
  const getClientLabel = (fieldKey) => {
    const schema = clientFieldsSchema[fieldKey];
    return schema?.label || fieldKey;
  };

  const getDebtLabel = (fieldKey) => {
    const schema = debtFieldsSchema[fieldKey];
    return schema?.label || fieldKey;
  };

  // Helper: get comma-separated group names
  const getGroupsValue = (client) => {
    const groups = [];
    if (client.FlowGroup) groups.push(client.FlowGroup.GroupName);
    if (client.YearGroup) groups.push(client.YearGroup.GroupName);
    if (client.GeneralGroups && client.GeneralGroups.length > 0) {
      groups.push(...client.GeneralGroups.map((g) => g.GroupName));
    }
    return groups.length > 0 ? groups.join(', ') : 'לא הוגדר';
  };

  // Helper: read the client data
  const getClientValue = (client, fieldKey) => {
    // Check if this is a groups field
    if (fieldKey.endsWith('Groups')) {
      return getGroupsValue(client);
    }
    // Regular client field
    return client[fieldKey] ?? '';
  };

  // Helper: read the debt data
  const getDebtValue = (client, fieldKey) => {
    if (fieldKey === 'TotalDebt') {
      return parseFloat(client?.debt?.TotalDebt || 0);
    }
    return parseFloat(client?.debt?.DebtFields?.[fieldKey] || 0);
  };

  // ============== Build raw HTML with dynamic columns ==============
  const generatePrintHTML = (clientsData) => {
    const style = `
      <style>
        @media print {
          @page {
            margin: 10mm;
          }
          thead {
            display: table-header-group;
          }
          tr {
            page-break-inside: avoid;
          }
        }
        table {
          border-collapse: collapse;
          width: 100%;
          font-family: Arial, sans-serif;
          direction: rtl;
        }
        th, td {
          border: 1px solid #ccc;
          padding: 4px;
          text-align: center;
          font-size: 16px;
        }
        th {
          background-color: #f0f0f0;
          font-weight: bold;
        }
      </style>
    `;

    // Build filter message with all active filters
    const filterParts = [];

    // Group filter message
    if (filteredGroup) {
      switch (filteredGroup) {
        case 'NO_FLOW':
          filterParts.push('ללא קבוצת שוטף');
          break;
        case 'NO_YEAR':
          filterParts.push('ללא קבוצת דו"ח שנתי');
          break;
        case 'NO_GENERAL':
          filterParts.push('ללא קבוצות כלליות');
          break;
        case 'NO_GROUP':
          filterParts.push('ללא כל קבוצה');
          break;
        default:
          filterParts.push(`קבוצה: ${groupNameParam || filteredGroup}`);
      }
    }

    // Debt filter messages (multiple filters)
    if (debtFiltersParam.length > 0) {
      debtFiltersParam.forEach((filter) => {
        if (filter.field && filter.threshold) {
          const fieldLabel = getDebtLabel(filter.field);
          const formattedThreshold = parseFloat(filter.threshold).toLocaleString('he-IL', {
            style: 'currency',
            currency: 'ILS',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          });
          filterParts.push(`${fieldLabel} >= ${formattedThreshold}`);
        }
      });
    }

    const filterSentence = filterParts.length > 0
      ? `<p style="font-size: 14px; font-family: Arial, sans-serif; direction: rtl;">מסונן: ${filterParts.join(' | ')}</p>`
      : '';

    // 1) "Row #"
    // 2) dynamic client columns from printClientDetails
    // 3) dynamic debt columns from debtFields

    // Build the table header
    const clientHeaders = printClientDetails
      .map((fieldKey) => `<th>${getClientLabel(fieldKey)}</th>`)
      .join('');

    const debtHeaders = debtFields
      .map((fieldKey) => `<th>${getDebtLabel(fieldKey)}</th>`)
      .join('');

    const tableHeader = `
      <thead>
        <tr>
          <th>מס'</th>
          ${clientHeaders}
          ${debtHeaders}
        </tr>
      </thead>
    `;

    // Build the rows
    const rowsHTML = clientsData
      .map((client, index) => {
        // client fields
        const clientCells = printClientDetails
          .map((fieldKey) => {
            const val = getClientValue(client, fieldKey);
            return `<td>${val}</td>`;
          })
          .join('');

        // debt fields
        const debtCells = debtFields
          .map((fieldKey) => {
            const val = getDebtValue(client, fieldKey);
            // Format as currency
            const formatted = val.toLocaleString('he-IL', {
              style: 'currency',
              currency: 'ILS',
            });
            return `<td>${formatted}</td>`;
          })
          .join('');

        return `
          <tr>
            <td>${index + 1}</td>
            ${clientCells}
            ${debtCells}
          </tr>
        `;
      })
      .join('');

    return `
      <!DOCTYPE html>
      <html dir="rtl" lang="he">
      <head>
        <meta charset="UTF-8" />
        ${style}
        <title>לקוחות</title>
      </head>
      <body>
        ${generateBusinessHeader(businessProfile)}
        ${filterSentence}
        <table>
          ${tableHeader}
          <tbody>
            ${rowsHTML}
          </tbody>
        </table>
      </body>
      </html>
    `;
  };

  // ============== RENDER STATES ==============
  if (isLoading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          טוען נתונים להדפסה...
        </Typography>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 3, color: 'red' }}>
        <Typography variant="h6">שגיאה בטעינת הלקוחות להדפסה</Typography>
        <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/clients', { replace: true })}>
          חזרה לרשימת הלקוחות
        </Button>
      </Box>
    );
  }

  // If loaded successfully, we show a minimal screen
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6">פתיחת חלון חדש להדפסה...</Typography>
      <Button variant="outlined" onClick={() => navigate('/clients', { replace: true })} sx={{ mt: 2 }}>
        חזרה לרשימת הלקוחות
      </Button>
    </Box>
  );
}

export default MinimalPrintPage;